import { deviceMetadata, deviceSignupMetadata } from "@/tracking/tracking/getDeviceData";
import { login } from "@/api/login";
import { register } from "@/api/register";
import { delAfterLoginRedirectURL, getAfterLoginRedirectURL } from "@/api/localStorage";
import type { RegisterFlagsT } from "@/api/login";
import { sendRmdTrxEvent } from "@/tracking/tracking/GTM";
import { EventTypes } from "@/cms/components/Common/Utilities/GoogleTagManager";

type WrongPartnerType = { link: string; partnerName: string };
type TranslationType = (key: string) => string;

export const parseLoginError = (
  err: string,
  desc: string,
  t: TranslationType,
  setWrongPartner: (partner: WrongPartnerType) => void
) => {
  if (err === "unauthorized") {
    if (desc?.startsWith("login.clientid.mismatch")) {
      const whiteLabel = desc.substring(desc.lastIndexOf(".") + 1);
      const partnerMap: Record<string, WrongPartnerType> = {
        medionApp: {
          link: "https://wechselservice.medion.com/login",
          partnerName: "MEDION",
        },
        focusApp: {
          link: "https://remindme.focus.de/login",
          partnerName: "Focus",
        },
        sfuchsApp: {
          link: "https://remindme.schnaeppchenfuchs.com/login",
          partnerName: "Schnäppchenfuchs",
        },
        default: {
          link: "https://www.remind.me/login",
          partnerName: "remind.me",
        },
      };

      setWrongPartner(partnerMap[whiteLabel] || partnerMap.default);
    } else if (desc === "account.blocked") {
      return t("auth.errors.accountBlocked");
    } else if (desc === "user.registration_not_confirmed") {
      return t("auth.errors.userRegistrationNotConfirmed");
    }
  } else if (err === "invalid_grant") {
    return t("auth.errors.wrongCredentials");
  }
  return t("auth.errors.unexpectedError");
};

export const handleAuthSuccess = () => {
  const customEvent = new CustomEvent("AuthStepSuccess", {});
  window.dispatchEvent(customEvent);
};

export const handleRegistration = async (
  email: string,
  password: string,
  pathname: string,
  flags: RegisterFlagsT = {},
  tracking: Record<string, unknown> = {}
) => {
  const urlParams = new URLSearchParams(window.location.search);
  const referralCode = urlParams.get("referralCode");

  const response = await register(email, password, pathname, deviceMetadata, deviceSignupMetadata, {
    ...flags,
    ...(tracking && typeof tracking === "object" && tracking),
    ...(referralCode && ({ referralCode } as RegisterFlagsT)),
  });

  const customEvent = new CustomEvent("AuthEvent", {
    detail: { userId: response, email, type: "REGISTER", platform: "email" },
  });
  window.dispatchEvent(customEvent);
  handleAuthSuccess();
  sendRmdTrxEvent(EventTypes.USER_CREATED, response, email);
  return response;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleLoginResponse = (response: any, email: string) => {
  if (response && !response?.error) {
    const customEvent = new CustomEvent("AuthEvent", {
      detail: { userId: response.user_id, email, platform: "email" },
    });
    window.dispatchEvent(customEvent);
    handleAuthSuccess();

    const urlAfterLogin = getAfterLoginRedirectURL();
    if (urlAfterLogin) {
      window.location.replace(urlAfterLogin);
      delAfterLoginRedirectURL();
    }
  }
};

export const handleLogin = async (email: string, password: string) => {
  const response = await login(email, password);
  handleLoginResponse(response, email);

  return response;
};

export const navigateTo = (path: string, queryParams?: Record<string, string>) => {
  const routeEvent = new CustomEvent("RouteEvent", { detail: { path, queryParams } });
  window.dispatchEvent(routeEvent);
};
